import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import {
  SessionDataRequestBody,
  useGetHealthQuery,
  usePostSessionDataMutation,
} from "../redux/services/config";
import { setOnlineMode } from "../redux/slices/applicationSlice";

import { getFailedSessionRequests, setFailedSessionRequests } from "../utils";

export const useOnlineMode = (sendOfflineEvent: () => void) => {
  const dispatch = useDispatch();
  const lastOnlineStatus = useRef<boolean | null>(null);

  const { fulfilledTimeStamp, startedTimeStamp, refetch, isFetching, isError } =
    useGetHealthQuery(undefined, { pollingInterval: 15000 });

  const [postSessionData] = usePostSessionDataMutation();

  const resendFailedSessionRequests = useCallback(() => {
    const pastFailedRequests = getFailedSessionRequests();
    const failedRequests: SessionDataRequestBody[] = [];

    if (pastFailedRequests.length) {
      pastFailedRequests.forEach((session: SessionDataRequestBody) => {
        postSessionData(session)
          .unwrap()
          .catch((error) => {
            if (error.status !== 400) failedRequests.push(session);
          });
      });

      setFailedSessionRequests(failedRequests);
    }
  }, [postSessionData]);

  const logConnectivityChange = useCallback((isOnline: boolean) => {
    console.info(
      `[useOnlineMode] Connectivity status: ${isOnline ? "online" : "offline"}`,
    );
  }, []);

  const handleOnline = useCallback(() => {
    if (lastOnlineStatus.current !== true) {
      logConnectivityChange(true);
      resendFailedSessionRequests();
    }
    lastOnlineStatus.current = true;
    dispatch(setOnlineMode(true));
  }, [dispatch, logConnectivityChange, resendFailedSessionRequests]);

  const handleOffline = useCallback(() => {
    if (lastOnlineStatus.current !== false) {
      logConnectivityChange(false);
      sendOfflineEvent();
    }
    lastOnlineStatus.current = false;
    dispatch(setOnlineMode(false));
  }, [dispatch, logConnectivityChange, sendOfflineEvent]);

  useEffect(() => {
    const offline = () => handleOffline();
    const online = () => {
      refetch();
    };
    window.addEventListener("offline", offline);
    window.addEventListener("online", online);
    return () => {
      window.removeEventListener("offline", offline);
      window.removeEventListener("online", online);
    };
  }, [handleOffline, refetch]);

  useEffect(() => {
    if (navigator.onLine === false) {
      handleOffline();
      return;
    }
    // Retain the previous status during a request; cached success must not
    // mark a reconnect online until the new health check has completed.
    if (isFetching) return;
    if (isError) handleOffline();
    else if (
      fulfilledTimeStamp &&
      startedTimeStamp &&
      fulfilledTimeStamp >= startedTimeStamp
    )
      handleOnline();
  }, [
    fulfilledTimeStamp,
    startedTimeStamp,
    isFetching,
    isError,
    handleOnline,
    handleOffline,
  ]);

  return { checkOnlineMode: refetch };
};
